import "react";
import {
  Zap,
  CreditCard,
  Brain,
  Webhook,
  ChevronRight,
  ExternalLink,
  Plug,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const integrations = [
  {
    id: "zapier",
    name: "Zapier",
    description:
      "Connect eScribe to 5,000+ apps including Salesforce, Google Sheets, Slack, and more.",
    icon: Zap,
    color: "orange",
    href: "/integrations/zapier",
    status: "unavailable",
    badge: "5,000+ apps",
  },
  {
    id: "webhooks",
    name: "Webhooks",
    description:
      "Send real-time notifications to your own endpoints when events occur.",
    icon: Webhook,
    color: "emerald",
    href: "/webhooks",
    status: "available",
    badge: null,
  },
  {
    id: "stripe",
    name: "Payment Collection",
    description: "Collect payments alongside signatures using Stripe.",
    icon: CreditCard,
    color: "purple",
    href: "/integrations/payments",
    status: "available",
    badge: "Stripe Connect",
  },
  {
    id: "ai",
    name: "AI Field Detection",
    description:
      "Automatically detect signature and form fields in your documents.",
    icon: Brain,
    color: "blue",
    href: "/integrations/ai",
    status: "available",
    badge: "Beta",
  },
];

const colorClasses = {
  orange: {
    bg: "bg-orange-500/10",
    text: "text-orange-600",
    border: "border-orange-200",
  },
  purple: {
    bg: "bg-purple-500/10",
    text: "text-purple-600",
    border: "border-purple-200",
  },
  blue: {
    bg: "bg-blue-500/10",
    text: "text-blue-600",
    border: "border-blue-200",
  },
  emerald: {
    bg: "bg-emerald-500/10",
    text: "text-emerald-600",
    border: "border-emerald-200",
  },
};

const IntegrationCard = ({ integration }) => {
  const { t } = useTranslation();
  const Icon = integration.icon;
  const colors = colorClasses[integration.color];
  const isAvailable = integration.status === "available";

  const content = (
    <Card
      className={`h-full transition-all ${
        isAvailable
          ? "hover:border-primary/50 hover:shadow-md cursor-pointer"
          : "opacity-60 cursor-not-allowed"
      }`}
    >
      {/* Header */}
      <div
        className={`h-20 ${colors.bg} flex items-center justify-center relative rounded-t-lg`}
      >
        <Icon className={`h-10 w-10 ${colors.text}`} />

        {/* Badge */}
        {integration.badge && (
          <div className="absolute top-2 right-2">
            <Badge
              variant={
                integration.status === "coming_soon" ? "secondary" : "outline"
              }
              className={
                integration.status !== "coming_soon"
                  ? `bg-white/90 ${colors.text} ${colors.border}`
                  : ""
              }
            >
              {integration.badge}
            </Badge>
          </div>
        )}

        {/* Status badge */}
        <div className="absolute bottom-2 left-2">
          {isAvailable ? (
            <Badge
              variant="outline"
              className="bg-emerald-500/10 text-emerald-600 border-emerald-200"
            >
              {t("integrations.active")}
            </Badge>
          ) : (
            <Badge variant="secondary">{t("integrations.comingSoon")}</Badge>
          )}
        </div>
      </div>

      {/* Content */}
      <CardContent className="p-4 dark:bg-background">
        <h3 className="font-semibold mb-1">{integration.name}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2">
          {integration.description}
        </p>

        {isAvailable && (
          <div className="mt-4 pt-3 border-t flex items-center justify-between text-sm text-primary">
            <span>{t("integrations.configure")}</span>
            <ChevronRight className="h-4 w-4" />
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (!isAvailable) {
    return content;
  }

  return (
    <Link to={integration.href} className="block h-full">
      {content}
    </Link>
  );
};

export const Integrations = () => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            {t("integrations.title")}
          </h1>
          <p className="text-muted-foreground">{t("integrations.subtitle")}</p>
        </div>
      </div>

      {/* Info card */}
      <Card className="bg-muted/50">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">
            {t("integrations.infoText")}
          </p>
        </CardContent>
      </Card>

      {/* Integrations grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {integrations.map((integration) => (
          <IntegrationCard key={integration.id} integration={integration} />
        ))}
      </div>

      {/* API Documentation Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-muted">
                <Plug className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold">
                  {t("integrations.buildCustom")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t("integrations.buildCustomDesc")}
                </p>
              </div>
            </div>
            <Button variant="outline" asChild>
              <a
                href="/api-docs.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("integrations.apiDocs")}
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Integrations;
